import React, { useMemo } from "react";
import {
  firstCapStartsAtPlaces,
  generateShapedPayoutTable,
} from "../../../utility/generateShapedPayoutTable";

const SHAPE_LINES = [
  { id: "first", name: "More to first", className: "stroke-brand-gold" },
  { id: "standard", name: "Standard", className: "stroke-brand-charcoal" },
  { id: "even", name: "More even", className: "stroke-brand-muted" },
];

const PayoutCurveChart = ({
  payoutData = [],
  places,
  shape,
  ratioNumerator,
  ratioDenominator,
}) => {
  const capAt = firstCapStartsAtPlaces(ratioNumerator, ratioDenominator);
  const maxPlaces = Math.max(
    3,
    Math.min(50, Number(places) || 30),
    ...payoutData.map((row) => Number(row.payoutQuantity) || 0)
  );

  const shapeFirsts = useMemo(() => {
    const series = {};
    SHAPE_LINES.forEach((item) => {
      const rows = generateShapedPayoutTable({
        maxPlaces,
        shape: item.id,
        payoutRatioNumerator: Number(ratioNumerator) || 1,
        payoutRatioDenominator: Number(ratioDenominator) || 6,
      });
      series[item.id] = Array.from({ length: maxPlaces }, (_, index) => {
        const first = rows.find(
          (row) =>
            row.payoutQuantity === index + 1 && row.payoutOrder === 1
        );
        return (first?.percentage || 0) * 100;
      });
    });
    return series;
  }, [maxPlaces, ratioNumerator, ratioDenominator]);

  const tableFirsts = useMemo(() => {
    const firstByColumn = new Map();
    payoutData.forEach((row) => {
      if (Number(row.payoutOrder) === 1) {
        firstByColumn.set(
          Number(row.payoutQuantity),
          Number(row.percentage) * 100
        );
      }
    });
    return Array.from({ length: maxPlaces }, (_, index) => {
      const value = firstByColumn.get(index + 1);
      return value == null ? null : { x: index + 1, y: value };
    }).filter(Boolean);
  }, [payoutData, maxPlaces]);

  const width = 320;
  const height = 148;
  const pad = { top: 8, right: 8, bottom: 22, left: 28 };
  const innerW = width - pad.left - pad.right;
  const innerH = height - pad.top - pad.bottom;
  const yMax = 70;
  const span = Math.max(maxPlaces - 1, 1);
  const xOf = (x) => pad.left + ((x - 1) / span) * innerW;
  const yOf = (value) =>
    pad.top + innerH - (Math.min(Math.max(value, 0), yMax) / yMax) * innerH;
  const ticks = [0, 20, 40, 60];
  const xTicks = [1, Math.min(8, maxPlaces), capAt, maxPlaces].filter(
    (value, index, all) =>
      value >= 1 && value <= maxPlaces && all.indexOf(value) === index
  );

  const toPoints = (values) =>
    values.map((point) => `${xOf(point.x)},${yOf(point.y)}`).join(" ");

  return (
    <div className="card-inset p-3 space-y-2">
      <p className="text-sm font-medium text-brand-charcoal">
        First place by places paid
      </p>
      <svg
        viewBox={`0 0 ${width} ${height}`}
        className="w-full h-52"
        role="img"
        aria-label="First place percent of the prize fund by how many places are paid. Three fill curves plus this table."
      >
        {ticks.map((tick) => (
          <g key={tick}>
            <line
              x1={pad.left}
              x2={width - pad.right}
              y1={yOf(tick)}
              y2={yOf(tick)}
              className="stroke-brand-charcoal/15"
              strokeWidth="1"
            />
            <text
              x={pad.left - 4}
              y={yOf(tick) + 3}
              textAnchor="end"
              className="fill-brand-muted text-[9px]"
            >
              {tick}%
            </text>
          </g>
        ))}
        {capAt <= maxPlaces ? (
          <line
            x1={xOf(capAt)}
            x2={xOf(capAt)}
            y1={pad.top}
            y2={pad.top + innerH}
            className="stroke-brand-muted"
            strokeWidth="1"
            strokeDasharray="3 3"
          />
        ) : null}
        {SHAPE_LINES.map((item) => (
          <polyline
            key={item.id}
            fill="none"
            points={toPoints(
              shapeFirsts[item.id].map((value, index) => ({
                x: index + 1,
                y: value,
              }))
            )}
            className={item.className}
            strokeWidth={item.id === shape ? 1.75 : 1.25}
            opacity={item.id === shape ? 0.55 : 0.28}
            strokeLinejoin="round"
            strokeLinecap="round"
          />
        ))}
        {tableFirsts.length ? (
          <polyline
            fill="none"
            points={toPoints(tableFirsts)}
            className="stroke-brand-gold"
            strokeWidth="2.5"
            strokeLinejoin="round"
            strokeLinecap="round"
          />
        ) : null}
        {xTicks.map((tick) => (
          <text
            key={tick}
            x={xOf(tick)}
            y={height - 6}
            textAnchor="middle"
            className="fill-brand-muted text-[9px]"
          >
            {tick}
          </text>
        ))}
      </svg>
      <div className="flex flex-wrap gap-3 text-xs text-brand-muted">
        <span className="inline-flex items-center gap-1.5">
          <span className="h-0.5 w-4 bg-brand-gold" />
          This table
        </span>
        <span className="inline-flex items-center gap-1.5 opacity-60">
          <span className="h-0.5 w-4 bg-brand-gold" />
          More to first
        </span>
        <span className="inline-flex items-center gap-1.5 opacity-60">
          <span className="h-0.5 w-4 bg-brand-charcoal" />
          Standard
        </span>
        <span className="inline-flex items-center gap-1.5 opacity-60">
          <span className="h-0.5 w-4 bg-brand-muted" />
          More even
        </span>
      </div>
      <p className="text-xs text-brand-muted">
        Light lines are the three fill curves. Gold is this table. Edit a
        first-place cell and gold moves.
        {capAt <= maxPlaces
          ? ` Dashed line is the 40% cap at ${capAt} places.`
          : ""}
      </p>
    </div>
  );
};

export default PayoutCurveChart;
